import {Button} from '@wandb/components';
import {type TooltipProps} from '@wandb/components/dist/components/Tooltip';
import React, {useCallback} from 'react';

import {type PanelQuickAddEvent} from '../../../services/analytics/panelQuickAddEvents';
import {useDispatch} from '../../../state/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {getDefaultPanelConfig} from '../../../util/panelbank';
import {maybePluralize} from '../../../util/uihelpers';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useSortAlphabetically} from '../Settings/hooks';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {type QuickAddKeys} from './PanelsQuickAddContext';

type AddBulkButtonProps = {
  searchedKeys: QuickAddKeys;
  keysWithNoPanels: QuickAddKeys;
  trackQuickAddBound: (event: Omit<PanelQuickAddEvent, 'scope'>) => void;
  onBulkAdd?: () => void;
  disabled?: boolean;
};

export const AddBulkButton = ({
  searchedKeys,
  keysWithNoPanels,
  trackQuickAddBound,
  onBulkAdd,
  disabled = false,
}: AddBulkButtonProps) => {
  const numKeysInList = searchedKeys.length;
  const numKeysToAdd = keysWithNoPanels.length;
  const isSomeKeysAdded = numKeysInList > numKeysToAdd;

  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const sortAlphabetically = useSortAlphabetically();

  const dispatch = useDispatch();
  const onAddBulkClick = useCallback(() => {
    trackQuickAddBound({action: 'Add bulk clicked'});
    if (targetSectionRef) {
      const panelConfigs = keysWithNoPanels.map(([key, spec]) =>
        getDefaultPanelConfig(key, spec)
      );
      dispatch(
        PanelBankSectionConfigActions.addPanelsByConfig(
          targetSectionRef,
          panelConfigs,
          {
            shouldAlphaInsert: sortAlphabetically,
            panelBankConfigRef,
          }
        )
      );
    } else {
      dispatch(
        PanelBankConfigActions.addPanelsBySpec(
          panelBankConfigRef,
          keysWithNoPanels.map(([key, spec]) => ({key, spec})),
          sortAlphabetically
        )
      );
    }
    onBulkAdd?.();
  }, [
    dispatch,
    keysWithNoPanels,
    onBulkAdd,
    panelBankConfigRef,
    sortAlphabetically,
    targetSectionRef,
    trackQuickAddBound,
  ]);

  let tooltip: TooltipProps | undefined;
  if (isSomeKeysAdded) {
    tooltip = {
      children: "Generate panels for keys that haven't been added yet.",
    };
  }

  return (
    <Button
      fullWidth
      onPress={onAddBulkClick}
      tooltip={tooltip}
      isDisabled={disabled}>
      {`Create ${maybePluralize(numKeysToAdd, 'panel', 's', true)}`}
    </Button>
  );
};
